import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FontSizeOutlined,
  NumberOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  BorderOutlined,
  AlignLeftOutlined,
  SwapOutlined,
  DragOutlined,
  CalculatorOutlined,
  UploadOutlined,
  DownOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
  EyeOutlined,
  GlobalOutlined,
  SaveOutlined,
  FunctionOutlined
} from "@ant-design/icons";
import { 
  Button, 
  Typography, 
  Tag, 
  Badge, 
  Tooltip, 
  Space, 
  Input, 
  Checkbox, 
  Divider, 
  Modal, 
  Form, 
  Select,
  message,
  Card,
  Row,
  Col,
  Radio,
  Empty,
  Spin,
  Alert
} from "antd";

const { Text } = Typography;
const { Option } = Select;

// Field types definition
export const FIELD_TYPES = [
  { type: "text", label: "Text", icon: <FontSizeOutlined />, color: "#1890ff" },
  { type: "number", label: "Number", icon: <NumberOutlined />, color: "#52c41a" },
  { type: "date", label: "Date", icon: <CalendarOutlined />, color: "#fa8c16" },
  { type: "checkbox", label: "Checkbox", icon: <CheckSquareOutlined />, color: "#722ed1" },
  { type: "radio", label: "Radio", icon: <BorderOutlined />, color: "#eb2f96" },
  { type: "heading", label: "Heading", icon: <FontSizeOutlined />, color: "#fa8c16" },
  { type: "paragraph", label: "Paragraph", icon: <AlignLeftOutlined />, color: "#13c2c2" },
  { type: "dropdown", label: "Dropdown", icon: <DownOutlined />, color: "#13c684" },
  { type: "file", label: "File Upload", icon: <UploadOutlined />, color: "#1890ff" },
  { type: "comparison", label: "Comparison", icon: <SwapOutlined />, color: "#fa8c16" },
  { type: "arithmetic", label: "Arithmetic", icon: <CalculatorOutlined />, color: "#52c41a" },
  { type: "formula", label: "Formula", icon: <FunctionOutlined />, color: "#eb2f96" }, // NEW FORMULA FIELD
];

// Helper functions
export const getFieldColor = (type) => {
  const field = FIELD_TYPES.find(f => f.type === type);
  return field?.color || "#1890ff";
};

export const getTranslatedFieldLabel = (type, language) => {
  const translations = {
    text: { english: "Text", marathi: "मजकूर" },
    number: { english: "Number", marathi: "संख्या" },
    date: { english: "Date", marathi: "तारीख" },
    checkbox: { english: "Checkbox", marathi: "चेकबॉक्स" },
    radio: { english: "Radio", marathi: "रेडिओ" },
    heading: { english: "Heading", marathi: "शीर्षक" },
    paragraph: { english: "Paragraph", marathi: "परिच्छेद" },
    dropdown: { english: "Dropdown", marathi: "ड्रॉपडाउन" },
    file: { english: "File Upload", marathi: "फाइल अपलोड" },
    comparison: { english: "Comparison", marathi: "तुलना" },
    arithmetic: { english: "Arithmetic", marathi: "अंकगणित" },
    formula: { english: "Formula", marathi: "सूत्र" } // NEW
  };
  return translations[type]?.[language] || translations[type]?.english || type;
};

// ==================== FORMULA CONFIGURATION MODAL ====================
export const FormulaConfigModal = ({ 
  visible, 
  onClose, 
  onSave, 
  language = 'english', 
  editingField = null,
  availableFields = [] // List of available number fields for formula
}) => {
  const [form] = Form.useForm();
  const [formula, setFormula] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [formulaError, setFormulaError] = useState('');
  
  // Available operations for quick insert
  const operations = [
    { label: '+', value: '+', desc: 'Addition' },
    { label: '-', value: '-', desc: 'Subtraction' },
    { label: '×', value: '*', desc: 'Multiplication' },
    { label: '÷', value: '/', desc: 'Division' },
    { label: '( )', value: '()', desc: 'Parentheses' },
    { label: 'SUM', value: 'SUM()', desc: 'Sum of values' },
    { label: 'AVG', value: 'AVG()', desc: 'Average' },
    { label: 'MIN', value: 'MIN()', desc: 'Minimum' },
    { label: 'MAX', value: 'MAX()', desc: 'Maximum' },
    { label: 'COUNT', value: 'COUNT()', desc: 'Count' },
    { label: 'ROUND', value: 'ROUND()', desc: 'Round' },
    { label: 'IF', value: 'IF()', desc: 'Condition' },
  ];
  
  useEffect(() => {
    if (editingField) {
      form.setFieldsValue({
        enLabel: editingField.enLabel || '',
        mrLabel: editingField.mrLabel || '',
        formula: editingField.formula || '',
        decimalPlaces: editingField.decimalPlaces || 2,
        showThousandsSeparator: editingField.showThousandsSeparator !== false,
        defaultValue: editingField.defaultValue || '',
        required: editingField.required || false,
        enHelpText: editingField.enHelpText || '',
        mrHelpText: editingField.mrHelpText || ''
      });
      setFormula(editingField.formula || '');
      evaluateFormula(editingField.formula || '');
    } else {
      setFormula('');
      setPreviewResult(null);
    }
  }, [editingField, form]);
  
  // Evaluate formula and show preview
  const evaluateFormula = (formulaStr, testValues = null) => {
    if (!formulaStr.trim()) {
      setPreviewResult(null);
      setFormulaError('');
      return;
    }
    
    try {
      // Create a safe evaluation environment
      let processedFormula = formulaStr;
      
      // Replace field references with test values or zeros
      availableFields.forEach(field => {
        const fieldPattern = new RegExp(`{{${field.id}}}`, 'g');
        const testValue = testValues?.[field.id] ?? 0;
        processedFormula = processedFormula.replace(fieldPattern, testValue);
      });
      
      // Replace function names with JavaScript equivalents
      processedFormula = processedFormula
        .replace(/SUM\(([^)]+)\)/g, (match, args) => {
          const values = args.split(',').map(v => evaluateExpression(v.trim()));
          return values.reduce((a, b) => a + b, 0);
        })
        .replace(/AVG\(([^)]+)\)/g, (match, args) => {
          const values = args.split(',').map(v => evaluateExpression(v.trim()));
          return values.reduce((a, b) => a + b, 0) / values.length;
        })
        .replace(/MIN\(([^)]+)\)/g, (match, args) => {
          const values = args.split(',').map(v => evaluateExpression(v.trim()));
          return Math.min(...values);
        })
        .replace(/MAX\(([^)]+)\)/g, (match, args) => {
          const values = args.split(',').map(v => evaluateExpression(v.trim()));
          return Math.max(...values);
        })
        .replace(/COUNT\(([^)]+)\)/g, (match, args) => {
          const values = args.split(',').map(v => evaluateExpression(v.trim()));
          return values.filter(v => v !== null && v !== '').length;
        })
        .replace(/ROUND\(([^,]+),([^)]+)\)/g, (match, num, decimals) => {
          return `Math.round(${evaluateExpression(num.trim())} * Math.pow(10, ${decimals.trim()})) / Math.pow(10, ${decimals.trim()})`;
        })
        .replace(/IF\(([^,]+),([^,]+),([^)]+)\)/g, (match, condition, trueVal, falseVal) => {
          return `(${evaluateExpression(condition.trim())} ? ${evaluateExpression(trueVal.trim())} : ${evaluateExpression(falseVal.trim())})`;
        });
      
      // Evaluate mathematical expression safely
      // eslint-disable-next-line no-new-func
      const result = new Function('return ' + processedFormula)();
      
      if (isNaN(result)) {
        setFormulaError('Invalid formula result');
        setPreviewResult(null);
      } else {
        setPreviewResult(result);
        setFormulaError('');
      }
    } catch (error) {
      setFormulaError('Invalid formula syntax');
      setPreviewResult(null);
    }
  };
  
  const evaluateExpression = (expr) => {
    try {
      // eslint-disable-next-line no-new-func
      return new Function('return ' + expr)();
    } catch {
      return 0;
    }
  };
  
  const handleFormulaChange = (value) => {
    setFormula(value);
    evaluateFormula(value);
    form.setFieldsValue({ formula: value });
  };
  
  const insertAtCursor = (insertText) => {
    const textarea = document.getElementById('formula-input');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newFormula = formula.substring(0, start) + insertText + formula.substring(end);
    handleFormulaChange(newFormula);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insertText.length, start + insertText.length);
    }, 0);
  };
  
  const insertField = (fieldId) => {
    insertAtCursor(`{{${fieldId}}}`);
  };
  
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!values.formula || values.formula.trim() === '') {
        message.warning(language === 'english' ? 'Please enter a formula' : 'कृपया सूत्र प्रविष्ट करा');
        return;
      }
      
      if (formulaError) {
        message.warning(language === 'english' ? 'Please fix formula errors' : 'कृपया सूत्र त्रुटी सुधारा');
        return;
      }
      
      const config = {
        ...values,
        type: 'formula'
      };
      
      onSave(config);
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };
  
  return (
    <Modal
      open={visible}
      onCancel={() => {
        onClose();
        form.resetFields();
        setFormula('');
        setPreviewResult(null);
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          onClose();
          form.resetFields();
          setFormula('');
          setPreviewResult(null);
        }}>
          {language === 'english' ? 'Cancel' : 'रद्द करा'}
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {editingField 
            ? (language === 'english' ? 'Update Formula' : 'सूत्र अद्यतनित करा')
            : (language === 'english' ? 'Add Formula' : 'सूत्र जोडा')}
        </Button>
      ]}
      width={800}
      title={
        <Space>
          <FunctionOutlined style={{ color: '#eb2f96' }} />
          <span style={{ fontSize: 16, fontWeight: 500 }}>
            {editingField 
              ? (language === 'english' ? 'Edit Formula Field' : 'सूत्र फील्ड संपादित करा')
              : (language === 'english' ? 'Add Formula Field' : 'सूत्र फील्ड जोडा')}
          </span>
        </Space>
      }
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* Labels */}
        <Card size="small" title={language === 'english' ? 'Labels' : 'लेबल्स'} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="enLabel"
                label="English Label"
                rules={[{ required: true, message: 'Please enter English label' }]}
              >
                <Input placeholder="e.g., Total Amount" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mrLabel"
                label="मराठी लेबल"
                rules={[{ required: true, message: 'कृपया मराठी लेबल प्रविष्ट करा' }]}
              >
                <Input placeholder="उदा., एकूण रक्कम" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        
        {/* Available Fields */}
        {availableFields.length > 0 && (
          <Card 
            size="small" 
            title={language === 'english' ? 'Available Number Fields' : 'उपलब्ध नंबर फील्ड्स'} 
            style={{ marginBottom: 16 }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {availableFields.map(field => (
                <Button
                  key={field.id}
                  size="small"
                  onClick={() => insertField(field.id)}
                  style={{
                    borderColor: '#52c41a',
                    color: '#52c41a'
                  }}
                >
                  {field.enLabel || field.label}
                </Button>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              {language === 'english' 
                ? 'Click on any field to insert into formula' 
                : 'सूत्रात घालण्यासाठी कोणत्याही फील्डवर क्लिक करा'}
            </div>
          </Card>
        )}
        
        {/* Operations */}
        <Card 
          size="small" 
          title={language === 'english' ? 'Operations' : 'ऑपरेशन्स'} 
          style={{ marginBottom: 16 }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {operations.map(op => (
              <Tooltip key={op.value} title={op.desc}>
                <Button
                  size="small"
                  onClick={() => insertAtCursor(op.value)}
                  style={{
                    fontFamily: 'monospace',
                    fontWeight: 'bold'
                  }}
                >
                  {op.label}
                </Button>
              </Tooltip>
            ))}
          </div>
        </Card>
        
        {/* Formula Input */}
        <Card 
          size="small" 
          title={language === 'english' ? 'Formula' : 'सूत्र'} 
          style={{ marginBottom: 16 }}
          extra={
            previewResult !== null && !formulaError && (
              <Tag color="green" style={{ fontSize: 12 }}>
                Preview: {previewResult}
              </Tag>
            )
          }
        >
          <Form.Item
            name="formula"
            rules={[{ required: true, message: language === 'english' ? 'Please enter formula' : 'कृपया सूत्र प्रविष्ट करा' }]}
            validateStatus={formulaError ? 'error' : ''}
            help={formulaError}
          >
            <Input.TextArea
              id="formula-input"
              rows={4}
              placeholder={language === 'english' 
                ? 'e.g., {{field1}} + {{field2}} * 1.18\nSUM({{field1}}, {{field2}}, {{field3}})\nIF({{field1}} > 100, {{field1}} * 0.9, {{field1}})' 
                : 'उदा., {{फील्ड1}} + {{फील्ड2}} * 1.18'}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
              onChange={(e) => handleFormulaChange(e.target.value)}
            />
          </Form.Item>
          
          <Alert
            message={language === 'english' ? 'Formula Help' : 'सूत्र मदत'}
            description={
              <div style={{ fontSize: 12 }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>{language === 'english' ? 'Use {{field_id}} to reference number fields' : 'नंबर फील्ड संदर्भित करण्यासाठी {{field_id}} वापरा'}</li>
                  <li>{language === 'english' ? 'Supports: +, -, *, /, (, )' : 'समर्थित: +, -, *, /, (, )'}</li>
                  <li>{language === 'english' ? 'Functions: SUM(), AVG(), MIN(), MAX(), COUNT(), ROUND(), IF()' : 'फंक्शन्स: SUM(), AVG(), MIN(), MAX(), COUNT(), ROUND(), IF()'}</li>
                  <li>{language === 'english' ? 'Example: {{price}} * {{quantity}} + {{tax}}' : 'उदाहरण: {{किंमत}} * {{प्रमाण}} + {{कर}}'}</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </Card>
        
        {/* Format Settings */}
        <Card size="small" title={language === 'english' ? 'Format Settings' : 'फॉरमॅट सेटिंग्ज'} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="decimalPlaces" label={language === 'english' ? 'Decimal Places' : 'दशांश स्थाने'}>
                <Select>
                  <Option value={0}>0</Option>
                  <Option value={1}>1</Option>
                  <Option value={2}>2</Option>
                  <Option value={3}>3</Option>
                  <Option value={4}>4</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="showThousandsSeparator" valuePropName="checked">
                <Checkbox>
                  {language === 'english' ? 'Show Thousands Separator' : 'हजार विभाजक दर्शवा'}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>
        
        {/* General Settings */}
        <Card size="small" title={language === 'english' ? 'General Settings' : 'सामान्य सेटिंग्ज'}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="defaultValue" label={language === 'english' ? 'Default Value' : 'डीफॉल्ट मूल्य'}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="required" valuePropName="checked">
                <Checkbox>
                  {language === 'english' ? 'Required Field' : 'आवश्यक फील्ड'}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>
        
        {/* Help Text */}
        <Card size="small" title={language === 'english' ? 'Help Text' : 'मदत मजकूर'} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="enHelpText" label="English Help Text">
                <Input.TextArea rows={2} placeholder="Help text for users" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mrHelpText" label="मराठी मदत मजकूर">
                <Input.TextArea rows={2} placeholder="वापरकर्त्यांसाठी मदत मजकूर" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
};

// ==================== FORMULA DISPLAY COMPONENT ====================
export const FormulaField = ({
  label,
  value,
  decimalPlaces = 2,
  showThousandsSeparator = true,
  required = false,
  language = 'english',
  helpText = '',
  errorMessage = ''
}) => {
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    
    let formatted = num.toFixed(decimalPlaces);
    
    if (showThousandsSeparator) {
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formatted = parts.join('.');
    }
    
    return formatted;
  };
  
  return (
    <div style={{ marginBottom: 16, width: '100%' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          marginBottom: 8, 
          fontWeight: 500, 
          fontSize: 14,
          color: '#1e293b'
        }}>
          {label}
          {required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
        </label>
      )}
      
      <div
        style={{
          padding: '10px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #e9ecef',
          fontSize: 16,
          fontWeight: 500,
          color: '#1e293b',
          fontFamily: 'monospace'
        }}
      >
        {formatNumber(value)}
      </div>
      
      {helpText && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
          {helpText}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN FIELD TYPES PALETTE COMPONENT ====================
const FieldTypesPalette = ({ 
  onAddField, 
  language = "english",
  existingColumns = [],
  showExistingPalette = false,
  className = "",
  style = {},
  onPreviewClick,
  autoTranslate = false,
  onAutoTranslateToggle,
  onTranslateForm,
  translating = false,
  onSave,
  saving = false,
  approvalStatus = 'pending',
  currentUser,
  formCreator,
  isSaveDisabled = false
}) => {
  
  const handleAddField = (type) => {
    console.log("FieldTypesPalette handleAddField called with type:", type);
    if (onAddField) {
      onAddField(type);
    } else {
      console.warn("onAddField prop is not provided to FieldTypesPalette");
    }
  };
  
  const handleDragStart = (e, column) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(column));
  };
  
  const handlePreview = () => {
    if (onPreviewClick) {
      onPreviewClick();
    } else {
      console.warn("onPreviewClick prop is not provided to FieldTypesPalette");
    }
  };
  
  const handleAutoTranslateToggle = () => {
    if (onAutoTranslateToggle) {
      onAutoTranslateToggle();
    }
  };
  
  const handleTranslateForm = () => {
    if (onTranslateForm) {
      onTranslateForm();
    }
  };
  
  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };
  
  // Check if save button should be disabled
  const getSaveButtonDisabled = () => {
    if (isSaveDisabled) return true;
    if (approvalStatus === 'approved' && currentUser?.id !== formCreator) return true;
    return false;
  };
  
  // Get save button text based on approval status
  const getSaveButtonText = () => {
    if (approvalStatus === 'pending') {
      return language === 'english' ? 'Submit for Approval' : 'मंजुरीसाठी सबमिट करा';
    }
    if (approvalStatus === 'rejected') {
      return language === 'english' ? 'Resubmit' : 'पुन्हा सबमिट करा';
    }
    return language === 'english' ? 'Save' : 'जतन करा';
  };
  
  return (
    <>
<div
  className={`field-types-palette ${className}`}
  style={{
    background: "linear-gradient(145deg, #a8daf3 0%, #053058 100%)",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    border: "1px solid #e9ecef",
    transition: "all 0.3s ease",
    ...style
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08), 0 0 0 2px rgba(168,218,243,0.5)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
  }}
>

        {/* Header with All Action Buttons */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: "1px solid #a4c0db",
          flexWrap: "wrap",
          gap: "8px"
        }}>
          <Space align="center" size={8}>
            <div style={{
              width: 4,
              height: 20,
              background: "linear-gradient(180deg, #228be6 0%, #7950f2 100%)",
              borderRadius: 2
            }} />
            <Text strong style={{ fontSize: "15px", color: "#1e293b" }}>
              {language === 'english' ? 'Field Types' : 'फील्ड प्रकार'}
            </Text>
          </Space>
          
          {/* Action Buttons Group */}
          <Space size={8} wrap>
            {/* Auto Translate Button */}
            <Button
              type={autoTranslate ? "primary" : "default"}
              icon={<GlobalOutlined />}
              onClick={handleAutoTranslateToggle}
              shape="round"
              size="small"
              style={{
                borderColor: autoTranslate ? "#52c41a" : "#d9d9d9",
                background: autoTranslate ? "#52c41a" : "white",
                color: autoTranslate ? "white" : "#666"
              }}
            >
              {autoTranslate ? 'Auto ON' : 'Auto OFF'}
            </Button>
            
            {/* Translate Button */}
            <Button
              icon={<GlobalOutlined />}
              onClick={handleTranslateForm}
              loading={translating}
              shape="round"
              size="small"
            >
              {language === 'english' ? 'Translate' : 'मराठी'}
            </Button>
            
            {/* Preview Button */}
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              shape="round"
              size="small"
              style={{
                borderColor: "#04203b",
                color: "#04203b"
              }}
            >
              {language === 'english' ? 'Preview' : 'पूर्वावलोकन'}
            </Button>
            
            {/* Save Button */}
            {/* <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              shape="round"
              size="small"
              style={{
                background: "#1c7ed6",
                border: "none",
                paddingLeft: 16,
                paddingRight: 16
              }}
              disabled={getSaveButtonDisabled()}
            >
              {getSaveButtonText()}
            </Button> */}
          </Space>
          
          {showExistingPalette && existingColumns.length > 0 && (
            <Badge count={existingColumns.length} style={{ backgroundColor: '#1e293b', fontSize: "11px" }} />
          )}
        </div>
        
        {/* Field Types Grid */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          alignItems: "center"
        }}>
          {!showExistingPalette ? (
            FIELD_TYPES.map(type => {
              const fieldColor = type.color;
              
              return (
                <Tooltip 
                  key={type.type} 
                  title={language === 'english' ? `Add ${type.label}` : `${getTranslatedFieldLabel(type.type, 'marathi')} जोडा`}
                >
                  <Button
                    onClick={() => handleAddField(type.type)}
                    className="field-type-chip"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "5px 12px",
                      height: "32px",
                      background: type.type === 'dropdown' ? "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)" : "#ffffff",
                      border: `1px solid ${fieldColor}`,
                      borderRadius: "20px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      fontSize: "13px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = `0 4px 12px ${fieldColor}25`;
                      e.currentTarget.style.borderColor = fieldColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.02)";
                      e.currentTarget.style.borderColor = fieldColor;
                    }}
                  >
                    <span style={{ color: fieldColor, fontSize: "14px", marginRight: "2px" }}>
                      {type.icon}
                    </span>
                    <span style={{ color: "#1e293b", fontWeight: type.type === 'dropdown' ? 500 : 400 }}>
                      {getTranslatedFieldLabel(type.type, language)}
                    </span>
                  </Button>
                </Tooltip>
              );
            })
          ) : (
            existingColumns.length > 0 ? (
              existingColumns.map((column, index) => {
                const fieldType = FIELD_TYPES.find(f => f.type === column.type) || FIELD_TYPES[0];
                return (
                  <Tooltip key={index} title={language === 'english' ? "Drag to add" : "जोडण्यासाठी ड्रॅग करा"}>
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, column)}
                      style={{
                        padding: "5px 12px",
                        background: "#ffffff",
                        border: "1px solid #e9ecef",
                        borderRadius: "20px",
                        cursor: "grab",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        userSelect: "none",
                        transition: "all 0.2s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        height: "32px"
                      }}
                    >
                      <DragOutlined style={{ color: fieldType.color, fontSize: "12px" }} />
                      <Text strong style={{ fontSize: "12px", color: "#1e293b" }}>{column.label}</Text>
                      <Tag color={fieldType.color} style={{ margin: 0, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                        {column.type}
                      </Tag>
                    </div>
                  </Tooltip>
                );
              })
            ) : (
              <div style={{ width: "100%", textAlign: "center", padding: "12px", background: "#f8f9fa", borderRadius: "8px", color: "#868e96", fontSize: "12px" }}>
                <Text type="secondary">
                  {language === 'english' ? 'No existing columns' : 'कोणतेही विद्यमान स्तंभ नाहीत'}
                </Text>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

// ==================== COMPACT VERSION ====================
export const CompactFieldTypesPalette = ({ 
  onAddField, 
  language = "english", 
  className = "", 
  style = {}, 
  onPreviewClick,
  autoTranslate = false,
  onAutoTranslateToggle,
  onTranslateForm,
  translating = false,
  onSave,
  saving = false,
  approvalStatus = 'pending',
  currentUser,
  formCreator,
  isSaveDisabled = false
}) => {
  const handleAddField = (type) => {
    onAddField(type);
  };
  
  const handlePreview = () => {
    if (onPreviewClick) {
      onPreviewClick();
    }
  };
  
  const handleAutoTranslateToggle = () => {
    if (onAutoTranslateToggle) {
      onAutoTranslateToggle();
    }
  };
  
  const handleTranslateForm = () => {
    if (onTranslateForm) {
      onTranslateForm();
    }
  };
  
  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };
  
  const getSaveButtonDisabled = () => {
    if (isSaveDisabled) return true;
    if (approvalStatus === 'approved' && currentUser?.id !== formCreator) return true;
    return false;
  };
  
  const getSaveButtonText = () => {
    if (approvalStatus === 'pending') {
      return language === 'english' ? 'Submit for Approval' : 'मंजुरीसाठी सबमिट करा';
    }
    if (approvalStatus === 'rejected') {
      return language === 'english' ? 'Resubmit' : 'पुन्हा सबमिट करा';
    }
    return language === 'english' ? 'Save' : 'जतन करा';
  };
  
  return (
    <div className={`compact-field-types-palette ${className}`} style={{
      background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: "12px",
      padding: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      border: "1px solid #e9ecef",
      ...style
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: "8px" }}>
        <Space align="center" size={6}>
          <div style={{ width: 3, height: 16, background: "linear-gradient(180deg, #228be6 0%, #7950f2 100%)", borderRadius: 1.5 }} />
          <Text strong style={{ fontSize: "13px", color: "#1e293b" }}>
            {language === 'english' ? 'Quick Fields' : 'द्रुत फील्ड'}
          </Text>
        </Space>
        
        {/* Action Buttons Group */}
        <Space size={4} wrap>
          <Button
            icon={<GlobalOutlined />}
            onClick={handleAutoTranslateToggle}
            shape="round"
            size="small"
            type={autoTranslate ? "primary" : "default"}
            style={{
              borderColor: autoTranslate ? "#52c41a" : "#d9d9d9",
              background: autoTranslate ? "#52c41a" : "white",
              color: autoTranslate ? "white" : "#666"
            }}
          >
            {autoTranslate ? 'ON' : 'OFF'}
          </Button>
          
          <Button
            icon={<GlobalOutlined />}
            onClick={handleTranslateForm}
            loading={translating}
            shape="round"
            size="small"
          />
          
          <Button
            icon={<EyeOutlined />}
            onClick={handlePreview}
            shape="round"
            size="small"
            style={{
              borderColor: "#04203b",
              color: "#04203b"
            }}
          />
          
          {/* <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            shape="round"
            size="small"
            style={{
              background: "#1c7ed6",
              border: "none"
            }}
            disabled={getSaveButtonDisabled()}
          >
            {getSaveButtonText()}
          </Button> */}
        </Space>
      </div>
      
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {FIELD_TYPES.slice(0, 8).map(type => (
          <Tooltip key={type.type} title={getTranslatedFieldLabel(type.type, language)}>
            <Button
              onClick={() => handleAddField(type.type)}
              icon={type.icon}
              size="small"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                padding: 0,
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                background: "#ffffff",
                color: type.color,
                fontSize: "16px",
                transition: "all 0.2s"
              }}
            />
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

// ==================== EXPORT DEFAULT ====================
export default FieldTypesPalette;